// frontend/src/pages/FridayFunZone.jsx
// Friday Fun Zone - WITH REJOIN SUPPORT

import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import ScribbleGame from '../../components/games/ScribbleGame';
import RacingGame from '../../components/games/RacingGame';
import TriviaGame from '../../components/games/TriviaGame';
import WordChainGame from '../../components/games/WordChainGame';
import CharadesGame from '../../components/games/CharadesGame';
import VoiceChat from '../../components/games/VoiceChat';

const FridayFunZone = () => {
    const [funZoneStatus, setFunZoneStatus] = useState(null);
    const [activeSessions, setActiveSessions] = useState([]);
    const [selectedGame, setSelectedGame] = useState(null);
    const [currentSession, setCurrentSession] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showJoinModal, setShowJoinModal] = useState(false);
    const [joinSessionCode, setJoinSessionCode] = useState('');
    const [leaderboard, setLeaderboard] = useState([]);
    const [myStats, setMyStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);

    const games = [
        {
            id: 'scribble',
            name: 'Scribble',
            icon: '🎨',
            description: 'Draw and guess! One player draws while others guess the word.',
            players: '2-10 players',
            color: 'from-pink-500 to-rose-500'
        },
        //{
        //    id: 'racing',
        //    name: 'Team Racing',
        //    icon: '🏎️',
        //    description: 'Race your cars! Answer questions to boost your speed.',
        //    players: '2-8 players',
        //    color: 'from-blue-500 to-cyan-500'
        //},
        //{
        //    id: 'trivia',
        //    name: 'Trivia Quiz',
        //    icon: '🧠',
        //    description: 'Test your knowledge! Fast fingers win.',
        //    players: '2-10 players',
        //    color: 'from-purple-500 to-pink-500'
        //},
        //{
        //    id: 'wordchain',
        //    name: 'Word Chain',
        //    icon: '🔗',
        //    description: 'Chain words together! Last letter starts next word.',
        //    players: '2-10 players',
        //    color: 'from-green-500 to-emerald-500'
        //},
        //{
        //    id: 'charades',
        //    name: 'Charades',
        //    icon: '🎭',
        //    description: 'Act it out! Teammates guess what you\'re acting.',
        //    players: '4-10 players',
        //    color: 'from-orange-500 to-red-500'
        //}
    ];

    useEffect(() => {
        fetchCurrentUser();
        checkFunZoneStatus();
        fetchActiveSessions();
        fetchLeaderboard();
        fetchMyStats();

        // Refresh every 30 seconds
        const interval = setInterval(() => {
            fetchActiveSessions();
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    const fetchCurrentUser = async () => {
        try {
            const response = await api.get('/auth/me');
            setCurrentUser(response.data.data);
        } catch (error) {
            console.error('Fetch user error:', error);
        }
    };

    const checkFunZoneStatus = async () => {
        try {
            const response = await api.get('/funzone/status');
            setFunZoneStatus(response.data.data);
        } catch (error) {
            console.error('Status check error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchActiveSessions = async () => {
        try {
            const response = await api.get('/funzone/sessions');
            setActiveSessions(response.data.data);
        } catch (error) {
            console.error('Fetch sessions error:', error);
        }
    };

    const fetchLeaderboard = async () => {
        try {
            const response = await api.get('/funzone/leaderboard?limit=10');
            setLeaderboard(response.data.data);
        } catch (error) {
            console.error('Fetch leaderboard error:', error);
        }
    };

    const fetchMyStats = async () => {
        try {
            const response = await api.get('/funzone/mystats');
            setMyStats(response.data.data);
        } catch (error) {
            console.error('Fetch stats error:', error);
        }
    };

    const handleCreateSession = async (gameType) => {
        try {
            const response = await api.post('/funzone/session', {
                gameType,
                maxPlayers: 10,
                totalRounds: 3
            });

            setCurrentSession(response.data.data);
            setSelectedGame(gameType);
            setShowCreateModal(false);
            fetchActiveSessions();
        } catch (error) {
            console.error('Create session error:', error);
            alert('Failed to create session');
        }
    };

    const handleJoinSession = async (sessionCode) => {
        try {
            const response = await api.post(`/funzone/session/${sessionCode}/join`, {
                nickname: currentUser?.name || ''
            });

            setCurrentSession(response.data.data);
            setSelectedGame(response.data.data.gameType);
            setShowJoinModal(false);
            setJoinSessionCode('');
            fetchActiveSessions();

            // Show rejoin message
            if (response.data.isRejoin) {
                alert('Welcome back! Rejoining game...');
            }
        } catch (error) {
            console.error('Join session error:', error);
            alert(error.response?.data?.message || 'Failed to join session');
        }
    };

    const handleRejoinSession = async (session) => {
        try {
            // First get the latest session state
            const response = await api.get(`/funzone/session/${session.sessionCode}`);

            // Then join (or rejoin)
            const joinResponse = await api.post(`/funzone/session/${session.sessionCode}/join`, {
                nickname: currentUser?.name || ''
            });

            setCurrentSession(joinResponse.data.data);
            setSelectedGame(joinResponse.data.data.gameType);
            fetchActiveSessions();

            alert('Rejoined successfully! 🎮');
        } catch (error) {
            console.error('Rejoin error:', error);
            alert(error.response?.data?.message || 'Failed to rejoin session');
        }
    };

    const handleLeaveSession = async () => {
        if (!currentSession) return;

        try {
            await api.post(`/funzone/session/${currentSession.sessionCode}/leave`);
            setCurrentSession(null);
            setSelectedGame(null);
            fetchActiveSessions();
        } catch (error) {
            console.error('Leave session error:', error);
        }
    };

    const isPlayerInSession = (session) => {
        if (!currentUser) return false;
        return session.players.some(p => p.user._id === currentUser._id);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white"></div>
            </div>
        );
    }

    // If game is selected, show game component
    if (selectedGame && currentSession) {
        const GameComponent = {
            scribble: ScribbleGame,
            racing: RacingGame,
            trivia: TriviaGame,
            wordchain: WordChainGame,
            charades: CharadesGame
        }[selectedGame];

        return (
            <div className="min-h-screen bg-gray-900">
                <GameComponent
                    session={currentSession}
                    onLeave={handleLeaveSession}
                    onRefresh={() => fetchActiveSessions()}
                />
                {/* Voice Chat Component */}
                <VoiceChat roomId={currentSession.voiceRoomId} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-600 to-red-600">
            <div className="max-w-7xl mx-auto p-6">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-6xl font-bold text-white mb-4 animate-bounce">
                        🎉 Friday Fun Zone! 🎉
                    </h1>

                    {funZoneStatus && (
                        <div className={`inline-block px-6 py-3 rounded-full text-xl font-bold ${funZoneStatus.isFriday
                                ? 'bg-green-500 text-white animate-pulse'
                                : 'bg-yellow-500 text-gray-900'
                            }`}>
                            {funZoneStatus.message}
                        </div>
                    )}

                    {!funZoneStatus?.isFriday && (
                        <p className="text-white text-lg mt-4 opacity-90">
                            Fun Zone is only active on Fridays! Come back then to play. 😊
                        </p>
                    )}
                </div>

                {/* Games Grid */}
                {funZoneStatus?.isFriday && (
                    <>
                        <div className="mb-8">
                            <h2 className="text-3xl font-bold text-white mb-6 text-center">
                                Choose Your Game 🎮
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {games.map(game => (
                                    <div
                                        key={game.id}
                                        className="bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:scale-105 transition cursor-pointer"
                                        onClick={() => {
                                            setSelectedGame(game.id);
                                            setShowCreateModal(true);
                                        }}
                                    >
                                        <div className={`h-32 bg-gradient-to-r ${game.color} flex items-center justify-center`}>
                                            <span className="text-7xl">{game.icon}</span>
                                        </div>
                                        <div className="p-6">
                                            <h3 className="text-2xl font-bold text-gray-800 mb-2">
                                                {game.name}
                                            </h3>
                                            <p className="text-gray-600 mb-3">{game.description}</p>
                                            <div className="flex items-center text-sm text-gray-500">
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                </svg>
                                                {game.players}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Join by Code Button */}
                            <div className="mt-8 text-center">
                                <button
                                    onClick={() => setShowJoinModal(true)}
                                    className="px-8 py-4 bg-white text-purple-600 rounded-full font-bold text-xl hover:bg-gray-100 transition shadow-lg"
                                >
                                    📝 Join Game with Code
                                </button>
                            </div>
                        </div>

                        {/* Active Sessions */}
                        {activeSessions.length > 0 && (
                            <div className="mb-8">
                                <h2 className="text-3xl font-bold text-white mb-6 text-center">
                                    Join Active Games 🎯
                                </h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {activeSessions.map(session => {
                                        const game = games.find(g => g.id === session.gameType);
                                        const isInSession = isPlayerInSession(session);
                                        const isFull = session.players.length >= session.maxPlayers;

                                        return (
                                            <div key={session._id} className="bg-white rounded-xl shadow-lg p-6">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-4xl">{game?.icon}</span>
                                                        <div>
                                                            <h3 className="font-bold text-lg">{game?.name}</h3>
                                                            <p className="text-sm text-gray-600">
                                                                Host: {session.host.name}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${session.status === 'waiting'
                                                            ? 'bg-yellow-100 text-yellow-800'
                                                            : 'bg-green-100 text-green-800'
                                                        }`}>
                                                        {session.status}
                                                    </span>
                                                </div>

                                                <div className="flex items-center justify-between mb-4">
                                                    <span className="text-sm text-gray-600">
                                                        👥 {session.players.length}/{session.maxPlayers} players
                                                    </span>
                                                    <span className="text-sm font-mono font-bold text-purple-600">
                                                        #{session.sessionCode}
                                                    </span>
                                                </div>

                                                {/* Show Rejoin button if player is in session */}
                                                {isInSession ? (
                                                    <button
                                                        onClick={() => handleRejoinSession(session)}
                                                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2 px-4 rounded-lg font-bold hover:from-green-700 hover:to-emerald-700 transition"
                                                    >
                                                        🔄 Rejoin Game
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleJoinSession(session.sessionCode)}
                                                        disabled={isFull}
                                                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-lg font-bold hover:from-purple-700 hover:to-pink-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {isFull ? '🔒 Full' : 'Join Game'}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Leaderboard */}
                        {leaderboard.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-2xl p-8">
                                <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                                    🏆 Leaderboard
                                </h2>

                                <div className="space-y-3">
                                    {leaderboard.slice(0, 10).map((entry, index) => (
                                        <div key={entry._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-4">
                                                <span className={`text-2xl font-bold ${index === 0 ? 'text-yellow-500' :
                                                        index === 1 ? 'text-gray-400' :
                                                            index === 2 ? 'text-orange-600' :
                                                                'text-gray-600'
                                                    }`}>
                                                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                                                </span>
                                                <div>
                                                    <p className="font-bold text-gray-800">{entry.user.name}</p>
                                                    <p className="text-sm text-gray-600">
                                                        {entry.totalGames} games • {entry.totalWins} wins
                                                    </p>
                                                </div>
                                            </div>
                                            <span className="text-2xl font-bold text-purple-600">
                                                {entry.totalScore} pts
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Create Session Modal */}
                {showCreateModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-8 max-w-md w-full">
                            <h2 className="text-2xl font-bold mb-4">Create Game Session</h2>
                            <p className="text-gray-600 mb-6">
                                Starting a new {games.find(g => g.id === selectedGame)?.name} game!
                            </p>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        setSelectedGame(null);
                                    }}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleCreateSession(selectedGame)}
                                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-lg font-bold hover:from-purple-700 hover:to-pink-700"
                                >
                                    Create Game
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Join by Code Modal */}
                {showJoinModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-8 max-w-md w-full">
                            <h2 className="text-2xl font-bold mb-4">Join Game with Code</h2>
                            <p className="text-gray-600 mb-6">
                                Enter the session code to join a game
                            </p>

                            <input
                                type="text"
                                value={joinSessionCode}
                                onChange={(e) => setJoinSessionCode(e.target.value.toUpperCase())}
                                placeholder="Enter code (e.g., ABC123)"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-6 text-center text-xl font-mono font-bold uppercase"
                                maxLength={6}
                            />

                            <div className="flex gap-4">
                                <button
                                    onClick={() => {
                                        setShowJoinModal(false);
                                        setJoinSessionCode('');
                                    }}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleJoinSession(joinSessionCode)}
                                    disabled={!joinSessionCode || joinSessionCode.length < 6}
                                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-lg font-bold hover:from-purple-700 hover:to-pink-700 disabled:opacity-50"
                                >
                                    Join Game
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FridayFunZone;